/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import GlobeIcon from '@atlaskit/icon/core/globe';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import { dark as darkTheme, light as lightTheme } from '@atlaskit/tokens/tokens-raw';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { cleanTokenName } from '../../utils/clean-token-name';
import type { Pairings as PairingsType, Token as TokenType } from '../data/types';

import CopyPasteBlock from './copy-paste-block';

const wrapperStyles = css({
	maxWidth: 'fit-content',
	padding: token('space.200'),
	border: `1px solid ${token('color.border')}`,
	//eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});

const tokenNameStyled = css({
	padding: `${token('space.050')} ${token('space.100')}`,
	background: token('color.background.neutral'),
	//eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
	color: token('color.text'),
	cursor: 'pointer',
	font: token('font.code'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		verticalAlign: 'middle',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		background: token('color.background.neutral.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':active': {
		background: token('color.background.neutral.pressed'),
	},
});

const exampleStyles = css({
	display: 'flex',
	padding: '36px 104px',
	alignItems: 'center',
	columnGap: token('space.100'),
	//eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
	font: token('font.heading.large'),
	fontWeight: token('font.weight.regular'),
	marginBlockEnd: token('space.200'),
});

const tokenNameWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-of-type)': {
		marginBlockEnd: token('space.100'),
	},
});
/**
 * __Pairing__
 *
 * Recommended pairings for the suggested token on the result panel.
 *
 */
const Pairing = ({ pairings }: { pairings: PairingsType }): React.JSX.Element => {
	const { colorMode } = useThemeObserver();
	const tokensList = colorMode === 'dark' ? darkTheme : lightTheme;
	const findTokenValue = (tokenName: string) => {
		const token = tokensList.find((token) => cleanTokenName(token.name) === tokenName) as TokenType;
		return token?.value;
	};

	return (
		<div css={wrapperStyles}>
			<div
				css={[
					exampleStyles,
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
					{
						border: `1px solid ${findTokenValue(pairings.border)}`,
						background: findTokenValue(pairings.background),
						color: findTokenValue(pairings.text),
					},
				]}
			>
				<GlobeIcon
					color={findTokenValue(pairings.icon) as NewCoreIconProps['color']}
					label="pairingIcon"
				/>
				Text
			</div>
			{Object.values(pairings)
				.sort()
				.map((tokenName) => (
					<div css={tokenNameWrapperStyles} key={tokenName}>
						<CopyPasteBlock
							text={tokenName}
							renderWrapper={(children) => <code css={tokenNameStyled}>{children}</code>}
						/>
					</div>
				))}
		</div>
	);
};

export default Pairing;
